// pages/Contact.js
import React, { useRef, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import emailjs from "@emailjs/browser";
import useAnimation from "../components/useAnimation";
import PageEnterOverlay from "../components/PageEnterOverlay";
import p2 from "../assets/photos/851995094751bd9962be7e2944827a2c.JPG";
import p4 from "../assets/photos/DSCF6910 2.JPG";
import p3 from "../assets/photos/DSCF8115.JPG";
import p5 from "../assets/photos/IMG_8815.JPG";
import p1 from "../assets/photos/bfe94595297c00f40748278838968c.JPG";
import {
  PAGE_FADE_UP,
  PAGE_REVEAL_DELAY,
  PAGE_REVEAL_DURATION,
  PAGE_REVEAL_EASE,
} from "../utils/pageReveal";

const PHOTOS = [p1, p2, p3, p4, p5];
const TILTS  = [-6, 5, -8, 4, -5];

function Contact() {
  const form = useRef();
  const [message, setMessage] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  // photo reveal — zero React re-renders: directly mutate img style via refs
  const zoneRef    = useRef(null);
  const imgRefs    = useRef([]);
  const revealedRef = useRef(0);
  const lastMsRef  = useRef(0);

  const handleMouseMove = useCallback((e) => {
    const now = Date.now();
    if (now - lastMsRef.current < 32) return; // ~30fps cap
    lastMsRef.current = now;
    if (!zoneRef.current) return;
    const rect  = zoneRef.current.getBoundingClientRect();
    const count = Math.min(
      Math.ceil(((e.clientX - rect.left) / rect.width) * PHOTOS.length),
      PHOTOS.length,
    );
    if (count <= revealedRef.current) return;
    revealedRef.current = count;
    imgRefs.current.forEach((img, i) => {
      if (!img) return;
      if (i < count) {
        img.style.opacity = "1";
        img.style.transform = `translateY(0px) scale(1) rotate(${TILTS[i]}deg)`;
      }
    });
  }, []);

  // email js
  const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
  const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
  const PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

  const sendEmail = (e) => {
    e.preventDefault();

    emailjs.sendForm(SERVICE_ID, TEMPLATE_ID, form.current, PUBLIC_KEY).then(
      (result) => {
        console.log("Email sent successfully ^v^", result.text);
        setMessage("Email sent successfully ^v^"); // notification after clicking send button
        setIsOpen(true);
        form.current.reset(); // clean the form after successfully sending
      },
      (error) => {
        console.log("Email fail to send T^T", error.text);
        setIsOpen(true);
        setMessage("Email fail to send T^T Plz try again"); // notification after clicking send button
      },
    );
  };
  const location = useLocation();
  const { isAnimating, animationMode } = useAnimation(
    1200,
    location.state?.fromTransition,
  );

  // resume
  // const googleDocLink = process.env.REACT_APP_GOOGLE_DOC_URL;
  const openPDF = () => {
    window.open("/Resume_Yu_Luo.pdf", "_blank");
  };
  return (
    <div className="relative min-h-screen">
      <div className="fixed inset-0 bg-midBrown -z-10" />
      <PageEnterOverlay
        show={isAnimating}
        mode={animationMode}
        direction="right"
        bgClass="bg-pink"
      />

      {/* Outer wrapper */}
      <div className="min-h-screen flex flex-col px-4 sm:px-8 md:px-12 lg:px-20 xl:px-28 pt-16 sm:pt-20 pb-10 text-pink">
        {/* ── Sticky title with photo fan tucked to the right ── */}
        <div className="sticky top-10 z-20 bg-midBrown py-3 -mx-4 sm:-mx-8 md:-mx-12 lg:-mx-20 xl:-mx-28 px-4 sm:px-8 md:px-12 lg:px-20 xl:px-28 mb-10 sm:mb-12 md:mb-14 overflow-visible">
          <motion.div
            variants={PAGE_FADE_UP}
            initial="hidden"
            animate="visible"
            transition={{
              delay: PAGE_REVEAL_DELAY,
              duration: PAGE_REVEAL_DURATION,
              ease: PAGE_REVEAL_EASE,
            }}
            className="flex items-start justify-between"
          >
            {/* Title text */}
            <div>
              <h1 className="font-extrabold text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl leading-tight">
                Thank you.
              </h1>
              <p className="font-semibold text-lg sm:text-xl md:text-2xl mt-1 pl-6 sm:pl-10 md:pl-28">
                for taking time to visit my website ˙Ⱉ˙ฅ
              </p>
            </div>

            {/* ── Photo reveal: slide mouse left→right to reveal photos side-by-side ── */}
            <div
              ref={zoneRef}
              className="hidden lg:flex flex-1 items-end gap-2 ml-6 cursor-crosshair self-stretch"
              onMouseMove={handleMouseMove}
            >
              {/* "slide →" hint when nothing revealed yet */}
              {/* {visibleCount === 0 && (
                <span className="text-pink/50 text-xs font-semibold tracking-widest select-none self-center">
                  ← slide →
                </span>
              )} */}

              {PHOTOS.map((src, i) => (
                <img
                  key={i}
                  ref={(el) => (imgRefs.current[i] = el)}
                  src={src}
                  alt=""
                  draggable={false}
                  className="object-cover rounded-xl shadow-xl shrink-0"
                  style={{
                    width: 130,
                    height: 172,
                    opacity: 0,
                    transform: "translateY(24px) scale(0.85) rotate(0deg)",
                    transition: "opacity 0.28s ease-out, transform 0.28s ease-out",
                    willChange: "transform, opacity",
                  }}
                />
              ))}
            </div>
          </motion.div>
        </div>

        {/* ── Content ── */}
        <motion.div
          variants={PAGE_FADE_UP}
          initial="hidden"
          animate="visible"
          transition={{
            delay: PAGE_REVEAL_DELAY + 0.12,
            duration: PAGE_REVEAL_DURATION,
            ease: PAGE_REVEAL_EASE,
          }}
          className="flex-1 flex flex-col md:justify-center"
        >
          <div className="flex flex-col md:flex-row gap-10 md:gap-16">
            {/* Contact info */}
            <div className="flex-1 text-beige text-base sm:text-lg space-y-6">
              <div>
                <p className="font-bold text-lightBrown text-sm tracking-widest mb-2">
                  MY EMAIL
                </p>
                <a
                  href="mailto:abbieluo.yu@gmail.com"
                  className="hover:underline pl-4"
                >
                  abbieluo.yu@gmail.com
                </a>
              </div>
              <div>
                <p className="font-bold text-lightBrown text-sm tracking-widest mb-2">
                  MY OTHER INFO
                </p>
                <div className="pl-4 flex gap-4">
                  <a
                    href="http://www.linkedin.com/in/abbieluoyu"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline"
                  >
                    LinkedIn
                  </a>
                  <span>|</span>
                  <a
                    href="https://github.com/abbiekuma"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline"
                  >
                    GitHub
                  </a>
                  <span>|</span>
                  <button onClick={openPDF} className="hover:underline">
                    Resume
                  </button>
                </div>
              </div>
            </div>

            {/* Send message form */}
            <div className="flex-1 flex flex-col">
              <h2 className="font-bold text-lightBrown text-sm tracking-widest mb-4">
                SEND MESSAGE TO ME :)
              </h2>
              <form
                className="flex flex-col gap-3"
                ref={form}
                onSubmit={sendEmail}
              >
                <div className="flex gap-3">
                  <div className="flex flex-col w-1/3">
                    <label className="font-bold pb-1 text-beige text-sm">
                      Name
                    </label>
                    <input
                      type="text"
                      name="from_name"
                      className="border-2 bg-darkBrown border-brown rounded-lg p-2 text-beige text-sm focus:ring focus:ring-pink focus:outline-none"
                      required
                    />
                  </div>
                  <div className="flex flex-col w-2/3">
                    <label className="font-bold pb-1 text-beige text-sm">
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      className="border-2 bg-darkBrown border-brown rounded-lg p-2 text-beige text-sm focus:ring focus:ring-pink focus:outline-none"
                      required
                    />
                  </div>
                </div>
                <div className="flex flex-col">
                  <label className="font-bold pb-1 text-beige text-sm">
                    Message
                  </label>
                  <textarea
                    name="message"
                    className="border-2 bg-darkBrown border-brown rounded-lg p-2 text-beige text-sm focus:ring focus:ring-pink focus:outline-none"
                    rows="5"
                    required
                  />
                </div>
                <div className="flex justify-center mt-1">
                  <button
                    type="submit"
                    className="bg-lightBrown w-1/2 py-2 rounded-xl font-black text-sm text-darkBrown hover:bg-darkBrown hover:text-lightBrown active:bg-pink active:text-darkBrown transition-colors"
                  >
                    Send
                  </button>
                </div>
              </form>

              {isOpen && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                  <div className="bg-beige p-6 rounded-3xl shadow-lg w-72 text-center border-brown border-4">
                    <p className="text-midBrown text-base font-semibold mb-4">
                      {message}
                    </p>
                    <button
                      onClick={() => setIsOpen(false)}
                      className="text-midBrown font-black px-4 py-1 bg-lightBrown rounded-xl border-2 border-midBrown hover:bg-darkBrown hover:border-brown hover:text-lightBrown active:bg-pink"
                    >
                      Close
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
export default Contact;
